import React from 'react';
import { Pen, Type, TextCursor, Calendar, CheckSquare } from 'lucide-react';
import FIELD_TYPES from './utils/fieldTypes';
import { normalizedToPixel } from './utils/coordinateUtils';

// Map field type icons
const ICON_MAP = {
  Pen: Pen,
  Type: Type,
  TextCursor: TextCursor,
  Calendar: Calendar,
  CheckSquare: CheckSquare,
};

const FieldOverlay = ({
  field,
  role,
  pageWidth,
  pageHeight,
  isSelected,
  isDragging,
  onMouseDown,
  onResizeStart,
}) => {
  const fieldType = FIELD_TYPES[field.type];
  const area = field.areas[0]; // Assuming single area per field

  // Convert normalized coordinates to pixels
  const pixelCoords = normalizedToPixel(area, pageWidth, pageHeight);

  const Icon = ICON_MAP[fieldType?.icon] || TextCursor;

  // Use role color if available, otherwise use field type color
  const displayColor = role?.color || fieldType?.color || '#3B82F6';
  const bgColor = `${displayColor}20`; // 12% opacity
  const borderColor = isSelected ? displayColor : `${displayColor}80`;

  const handleMouseDown = (e) => {
    e.stopPropagation();
    onMouseDown(field.uuid, area, e);
  };

  const handleResizeMouseDown = (handle, e) => {
    e.stopPropagation();
    onResizeStart(field.uuid, area, e, handle);
  };

  return (
    <div
      className={`
        absolute cursor-move select-none
        ${isDragging ? 'opacity-75' : ''}
        ${isSelected ? 'z-20' : 'z-10'}
      `}
      style={{
        left: `${pixelCoords.x}px`,
        top: `${pixelCoords.y}px`,
        width: `${pixelCoords.w}px`,
        height: `${pixelCoords.h}px`,
        backgroundColor: bgColor,
        border: `2px solid ${borderColor}`,
        borderRadius: '4px',
        boxShadow: isSelected ? `0 0 0 2px ${displayColor}40` : 'none',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Field content */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden p-1">
        <Icon
          className="w-4 h-4 flex-shrink-0"
          style={{ color: displayColor }}
        />
        {pixelCoords.w > 80 && (
          <span
            className="ml-1 text-xs font-medium truncate"
            style={{ color: displayColor }}
          >
            {field.name}
          </span>
        )}
      </div>

      {/* Role indicator badge */}
      {role && pixelCoords.w > 60 && (
        <div
          className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-medium text-white rounded-full shadow-sm"
          style={{ backgroundColor: displayColor }}
        >
          {role.name.charAt(0)}
        </div>
      )}

      {/* Resize handles (only shown when selected) */}
      {isSelected && (
        <>
          {/* Northwest */}
          <div
            className="absolute -top-1 -left-1 w-3 h-3 bg-white border-2 rounded-sm cursor-nw-resize"
            style={{ borderColor: displayColor }}
            onMouseDown={(e) => handleResizeMouseDown('nw', e)}
          />
          {/* Northeast */}
          <div
            className="absolute -top-1 -right-1 w-3 h-3 bg-white border-2 rounded-sm cursor-ne-resize"
            style={{ borderColor: displayColor }}
            onMouseDown={(e) => handleResizeMouseDown('ne', e)}
          />
          {/* Southwest */}
          <div
            className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border-2 rounded-sm cursor-sw-resize"
            style={{ borderColor: displayColor }}
            onMouseDown={(e) => handleResizeMouseDown('sw', e)}
          />
          {/* Southeast */}
          <div
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 rounded-sm cursor-se-resize"
            style={{ borderColor: displayColor }}
            onMouseDown={(e) => handleResizeMouseDown('se', e)}
          />
        </>
      )}
    </div>
  );
};

export default FieldOverlay;
